import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { enviarContacto } from '../firebase/firestore';

const SERVICIOS_OPTIONS = [
  'Trauma y PTSD',
  'Ansiedad y Estrés',
  'Duelo y Pérdidas',
  'Terapia de Pareja',
  'Autoestima e Identidad',
  'Otro',
];

const PHONE_REGEX = /^(?:\+34\s?)?[6789]\d{2}\s?\d{3}\s?\d{3}$/;

export default function Contacto() {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const [estado, setEstado] = useState(null);
  const navigate = useNavigate();

  const onSubmit = async (data) => {
    setEstado('enviando');
    try {
      await enviarContacto({
        ...data,
        rgpdAceptado: true,
        fechaConsentimiento: new Date().toISOString(),
      });
      setEstado('ok');
      reset();
      setTimeout(() => navigate('/'), 4000);
    } catch (err) {
      console.error(err);
      setEstado('error');
    }
  };

  const INPUT_CLS =
    'w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-teal-500 transition-colors';
  const LABEL_CLS =
    'block text-xs uppercase tracking-widest text-stone-500 mb-2';

  return (
    <div className="pt-24">
      {/* Header */}
      <section className="py-20 bg-teal-950 text-white">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-teal-400 text-xs uppercase tracking-widest mb-4">
            Hablemos
          </p>
          <h1 className="font-display text-5xl md:text-6xl mb-6">
            Agenda tu consulta
          </h1>
          <p className="text-teal-200 text-lg max-w-xl leading-relaxed">
            La primera consulta es gratuita. Cuéntanos lo que necesitas y
            nos pondremos en contacto contigo.
          </p>
        </div>
      </section>

      {/* Formulario */}
      <section className="py-24 bg-stone-50">
        <div className="max-w-2xl mx-auto px-6">

          {estado === 'ok' ? (
            <div className="text-center py-16">
              <div className="text-5xl mb-4">✅</div>
              <h2 className="font-display text-3xl text-stone-800 mb-3">
                Mensaje enviado
              </h2>
              <p className="text-stone-500">
                Te contactaremos en las próximas 24 horas. Redirigiendo...
              </p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="space-y-6 bg-white p-10 rounded-2xl shadow-sm border border-stone-100"
            >
              <h2 className="font-display text-2xl text-stone-800 mb-2">
                Formulario de contacto
              </h2>

              {/* Nombre + Email */}
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className={LABEL_CLS}>Nombre *</label>
                  <input
                    {...register('nombre', { required: 'El nombre es obligatorio' })}
                    className={INPUT_CLS}
                    placeholder="Tu nombre"
                  />
                  {errors.nombre && (
                    <p className="text-red-500 text-xs mt-1">{errors.nombre.message}</p>
                  )}
                </div>
                <div>
                  <label className={LABEL_CLS}>Email *</label>
                  <input
                    {...register('email', {
                      required: 'El email es obligatorio',
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: 'Email inválido',
                      },
                    })}
                    className={INPUT_CLS}
                    placeholder="tu@email.com"
                  />
                  {errors.email && (
                    <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>
                  )}
                </div>
              </div>

              {/* Teléfono */}
              <div>
                <label className={LABEL_CLS}>Teléfono *</label>
                <input
                  {...register('telefono', {
                    required: 'El teléfono es obligatorio',
                    pattern: {
                      value: PHONE_REGEX,
                      message:
                        'Formato español requerido: +34 612 345 678 o 612345678',
                    },
                  })}
                  className={INPUT_CLS}
                  placeholder="+34 612 345 678"
                />
                {errors.telefono && (
                  <p className="text-red-500 text-xs mt-1">{errors.telefono.message}</p>
                )}
              </div>

              {/* Servicio */}
              <div>
                <label className={LABEL_CLS}>Servicio de interés</label>
                <select
                  {...register('servicio')}
                  className={`${INPUT_CLS} bg-white`}
                >
                  <option value="">Seleccionar...</option>
                  {SERVICIOS_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              {/* Mensaje */}
              <div>
                <label className={LABEL_CLS}>Mensaje *</label>
                <textarea
                  {...register('mensaje', { required: 'El mensaje es obligatorio' })}
                  rows={5}
                  className={`${INPUT_CLS} resize-none`}
                  placeholder="Cuéntanos brevemente qué te trae aquí..."
                />
                {errors.mensaje && (
                  <p className="text-red-500 text-xs mt-1">{errors.mensaje.message}</p>
                )}
              </div>

              {/* ── RGPD ─────────────────────────────────────────── */}
              <div className="bg-stone-50 border border-stone-200 rounded-xl p-4">
                <label className="flex items-start gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    {...register('rgpd', {
                      required:
                        'Debes aceptar la política de privacidad para continuar',
                    })}
                    className="mt-0.5 w-4 h-4 accent-teal-600 flex-shrink-0"
                  />
                  <span className="text-sm text-stone-600 leading-relaxed">
                    He leído y acepto la{' '}
                    <a
                      href="/privacidad"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-teal-700 underline hover:text-teal-900 transition-colors"
                    >
                      Política de Privacidad
                    </a>
                    . Consiento el tratamiento de mis datos personales
                    (nombre, email y teléfono) con la finalidad de gestionar
                    mi consulta. Puedo ejercer mis derechos de acceso,
                    rectificación y supresión escribiendo a{' '}
                    <span className="text-teal-700">
                      hola@serenidadpsychology.com
                    </span>
                    . *
                  </span>
                </label>
                {errors.rgpd && (
                  <p className="text-red-500 text-xs mt-2 ml-7">
                    {errors.rgpd.message}
                  </p>
                )}
              </div>

              {/* Nota informativa RGPD */}
              <p className="text-xs text-stone-400 leading-relaxed">
                <strong>Responsable:</strong> Clínica Serenidad Psychology ·{' '}
                <strong>Finalidad:</strong> Gestión de consultas y citas ·{' '}
                <strong>Legitimación:</strong> Consentimiento del interesado ·{' '}
                <strong>Derechos:</strong> Acceso, rectificación, supresión y
                portabilidad en hola@serenidadpsychology.com ·{' '}
                <strong>Más info:</strong>{' '}
                <a
                  href="/privacidad"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-stone-600"
                >
                  Política de Privacidad
                </a>
              </p>

              {estado === 'error' && (
                <p className="text-red-500 text-sm bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                  Hubo un error al enviar. Intenta nuevamente.
                </p>
              )}

              <button
                type="submit"
                disabled={estado === 'enviando'}
                className="btn-primary w-full text-center disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {estado === 'enviando' ? 'Enviando...' : 'Enviar mensaje'}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}